import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { LOGO_URL } from "../data/siteData";
import { toast } from "sonner";
import {
    LogOut, Inbox, Users, Briefcase, FileText, Plus, Pencil, Trash2,
    X, Loader2, BarChart3, ExternalLink, ScrollText, Settings as SettingsIcon, Search, KeyRound,
    Sparkles, Shield, Mails, FileSignature, Smartphone, TrendingUp, MapPin, Target, Star, Camera, Mail, GraduationCap, CreditCard,
    Menu,
} from "lucide-react";

import LeadsView from "../components/admin/LeadsView";
import AssignmentsView from "../components/admin/AssignmentsView";
import SettingsPanel from "../components/admin/SettingsPanel";
import LeadDeliveryPanel from "../components/admin/LeadDeliveryPanel";
import SeoMetaPanel from "../components/admin/SeoMetaPanel";
import ProfilePanel from "../components/admin/ProfilePanel";
import MobileBottomNav from "../components/admin/MobileBottomNav";
import MobileInbox from "../components/admin/MobileInbox";
import MobileAnalytics from "../components/admin/MobileAnalytics";
import MobileManage from "../components/admin/MobileManage";
import AdminMobileFab from "../components/admin/AdminMobileFab";
import AdminOrientationGuard from "../components/admin/AdminOrientationGuard";
import CreateDemoLeadModal from "../components/admin/CreateDemoLeadModal";
import UnifiedInbox from "../components/admin/UnifiedInbox";
import LoginActivityPanel from "../components/admin/LoginActivityPanel";
import AiMarketingPanel from "../components/admin/AiMarketingPanel";
import PaymentConsentsPanel from "../components/admin/PaymentConsentsPanel";
import PaymentSubmissionsPanel from "../components/admin/PaymentSubmissionsPanel";
import PaymentLinksPanel from "../components/admin/PaymentLinksPanel";
import InvoicesPanel from "../components/admin/InvoicesPanel";
import UniversalSearch from "../components/admin/UniversalSearch";
import LeadNotificationBell from "../components/admin/LeadNotificationBell";
import AppLeadsView from "../components/admin/AppLeadsView";
import SeoInsightsPanel from "../components/admin/SeoInsightsPanel";
import GbpProfilesPanel from "../components/admin/GbpProfilesPanel";
import ConversionsPanel from "../components/admin/ConversionsPanel";
import ReviewsModerationPanel from "../components/admin/ReviewsModerationPanel";
import PhotoManagementPanel from "../components/admin/PhotoManagementPanel";
import TutorEmailTemplatesPanel from "../components/admin/TutorEmailTemplatesPanel";
import TeamManagementPanel from "../components/admin/TeamManagementPanel";
import LeadManagementPanel from "../components/admin/LeadManagementPanel";
import MasterDataPanel from "../components/admin/MasterDataPanel";
import UnresolvedCitiesPanel from "../components/admin/UnresolvedCitiesPanel";
import TutorPortalSettings from "../components/admin/TutorPortalSettings";
import OngoingTuitionsPanel from "../components/admin/OngoingTuitionsPanel";
import AdminPwaInstallBanner from "../components/admin/AdminPwaInstallBanner";
import BulkDeleteSection from "../components/admin/BulkDeleteSection";

import { SuperModeProvider, useSuperMode } from "../context/SuperModeContext";
import SuperModeToggle, { SuperModeBadge } from "../components/admin/SuperModeToggle";

import { GitBranch as GitBranchIcon, Database as DatabaseIcon, BookOpen as BookOpenIcon, Bell } from "lucide-react";
import { Globe2 as Globe2Icon } from "lucide-react";
import TimeConverterPanel from "../components/admin/TimeConverterPanel";
import FeeRemindersPanel from "../components/admin/FeeRemindersPanel";
import FeeRecommendationPanel from "../components/admin/FeeRecommendationPanel";
import { Calculator as CalculatorIcon } from "lucide-react";
import { BellRing as BellRingIcon } from "lucide-react";

const NORMAL_MODE_TAB_IDS = new Set([
    "stats", "time-converter", "conversions", "unified-inbox", "inquiries",
    "registrations", "tutor-applications", "app-leads", "payment-submissions", "payment-links", "fee-reminders", "fee-recommendation", "invoices", "profile",
    // V85 — Makeover Leads is a lead-management surface. Reveal in Normal
    // Mode so authorised admins can respond to enquiries without switching
    // to Super Admin Mode (super-mode remained overkill for a CRM inbox).
    "makeover-leads",
    // V149 — Data-quality view: read-only, safe outside super mode.
    "unresolved-cities",
    // V156 — Post-tutor-acceptance workflow. Coordinators must reach this in
    // Normal Mode; Super Admin Mode is reserved for destructive operations.
    "assignments",
    // V176 — Lead Notifications is a day-to-day operational panel: admins
    // need to toggle channels + manage WhatsApp recipients + inspect the
    // delivery log while on the floor. The sensitive WABA credential input
    // is still masked server-side (never returned in plain text to the
    // browser), so exposing the panel in Normal Mode is safe.
    "lead-delivery",
]);

const NAV = [
    {
        group: "Operations",
        items: [
            { id: "stats", label: "Overview", icon: BarChart3 },
            { id: "time-converter", label: "International Time Converter", icon: Globe2Icon },
            { id: "conversions", label: "Conversion Insights", icon: Target },
            { id: "reviews", label: "Parent Reviews", icon: Star },
            { id: "unified-inbox", label: "Unified Inbox", icon: Mails },
            { id: "inquiries", label: "Inquiries", icon: Inbox },
            { id: "registrations", label: "Registrations", icon: Users },
            { id: "assignments", label: "Classroom & Assignments", icon: GraduationCap },
            { id: "tutor-applications", label: "Tutor Applications", icon: Briefcase },
            { id: "payment-consents", label: "Payment Consents", icon: FileSignature },
            { id: "payment-submissions", label: "Payment Submissions", icon: CreditCard },
            { id: "payment-links", label: "Payment Links", icon: CreditCard },
            { id: "fee-reminders", label: "Fee Reminders", icon: BellRingIcon },
            { id: "fee-recommendation", label: "Fee Recommendation", icon: CalculatorIcon },
            { id: "invoices", label: "Invoices", icon: FileText },
            { id: "app-leads", label: "App Leads", icon: Smartphone },
            // V85 — Makeover Leads moved into Operations (was under Content /
            // Super-Mode). Route + panel unchanged; access permission relaxed.
            { id: "makeover-leads", label: "Makeover Leads", icon: Sparkles, href: "/admin/makeover-leads" },
        ],
    },
    {
        group: "Content",
        items: [
            { id: "blogs", label: "Blogs", icon: FileText },
            { id: "policies", label: "Policies", icon: ScrollText },
            { id: "seo", label: "SEO Meta", icon: Search },
            { id: "seo-insights", label: "SEO Insights", icon: TrendingUp },
            { id: "seo-audit", label: "SEO Audit", icon: Shield, href: "/admin/seo-audit" },
            { id: "gbp-profiles", label: "Google Business", icon: MapPin },
            { id: "team-management", label: "Team Management", icon: Users },
            { id: "tutor-email-templates", label: "Tutor Email Templates", icon: Mail },
            { id: "ai-marketing", label: "Marketing Templates", icon: Sparkles },
        ],
    },
    {
        group: "System",
        items: [
            { id: "settings", label: "Site Settings", icon: SettingsIcon },
            { id: "lead-delivery", label: "Lead Notifications", icon: Bell },
            { id: "master-data", label: "Master Data", icon: DatabaseIcon },
            { id: "unresolved-cities", label: "Unresolved Cities", icon: MapPin },
            { id: "photo-management", label: "Photo Management", icon: Camera },
            { id: "login-activity", label: "Login Activity", icon: Shield },
            { id: "bulk-delete", label: "Bulk Delete", icon: Trash2 },
            { id: "profile", label: "My Profile", icon: KeyRound },
        ],
    },
];

// V39 — Modules temporarily hidden from main navigation but kept fully
// functional in the backend. Accessible only via:
//   Site Settings → Advanced Modules (Super Mode required).
// Once each module is feature-complete and tested it will move back into
// the main NAV above. Do NOT delete the underlying routes, panels, or APIs.
const HIDDEN_DEV_MODULES = [
    { id: "pipeline", label: "Lead Pipeline", icon: GitBranchIcon,
      desc: "BookMyTutor-style flat 6-stage CRM (Leads → Postings → Demos → Tuitions → Completed → Closed)." },
    { id: "ongoing-tuitions", label: "Ongoing Tuitions", icon: BookOpenIcon,
      desc: "Attendance, fee tracking and active-class roster for matched tutors." },
    { id: "tutor-portal", label: "Tutor Portal", icon: GraduationCap,
      desc: "Tutor PWA settings — registration source toggle, login visibility, download CTA." },
];

export default function AdminDashboard() {
    return (
        <SuperModeProvider>
            <AdminDashboardInner />
        </SuperModeProvider>
    );
}

function AdminDashboardInner() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const superMode = useSuperMode();
    const [tab, setTab] = useState("stats");
    const [mobileTab, setMobileTab] = useState("home"); // home | analytics | inbox | manage
    // V135.1 — Auto-open signal from Universal Search. Each click carries
    // { tabId, id, seq } — the seq counter ensures re-clicking the same
    // record still re-fires the effect in the target panel.
    const [autoOpen, setAutoOpen] = useState({ tabId: null, id: null, seq: 0 });
    const [inboxCount, setInboxCount] = useState(0);
    const [liveClassCount, setLiveClassCount] = useState(0);
    const [createLeadOpen, setCreateLeadOpen] = useState(false);  // V21 Phase 1 — FAB-triggered modal
    // V173 — Mobile sidebar drawer. Opens on hamburger tap; closes on
    // Escape, backdrop click, or tab selection. Desktop (lg+) ignores this
    // state entirely because the fixed sidebar is always visible there.
    const [drawerOpen, setDrawerOpen] = useState(false);
    // V201 — "Fee recommendation" from a demo lead opens the module pre-filled.
    const [feeRecPrefill, setFeeRecPrefill] = useState(null);
    useEffect(() => {
        const onOpen = (e) => { setFeeRecPrefill({ ...e.detail, _seq: Date.now() }); setTab("fee-recommendation"); setMobileTab("manage"); };
        window.addEventListener("fee-rec:open", onOpen);
        return () => window.removeEventListener("fee-rec:open", onOpen);
    }, []);

    // Build the visible NAV based on super-mode state. In Normal Mode the user
    // only sees a curated subset; Super Mode reveals everything.
    const visibleNav = NAV.map((group) => ({
        ...group,
        items: superMode.active ? group.items : group.items.filter((i) => NORMAL_MODE_TAB_IDS.has(i.id)),
    })).filter((g) => g.items.length > 0);

    // If super mode locks while the user is on a sensitive tab, snap them back home.
    useEffect(() => {
        if (!superMode.active && !NORMAL_MODE_TAB_IDS.has(tab)) {
            setTab("stats");
        }
    }, [superMode.active, tab]);

    // V175 — Reset scroll to top of the main content on every navigation.
    // The admin layout uses WINDOW scroll (not a nested overflow container),
    // so when the user had scrolled far down the sidebar+main and clicked a
    // tab lower in the sidebar, the new panel mounted far above the viewport
    // and required manual scroll-up. We now snap scroll to 0 synchronously
    // on tab/mobileTab change. Also resets any potential future nested main
    // scroll container (defensive — matches `[data-admin-scroll]`).
    useEffect(() => {
        try {
            window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        } catch {
            // Older browsers reject behavior:"instant" — fall back to legacy.
            window.scrollTo(0, 0);
        }
        document.querySelectorAll("[data-admin-scroll]").forEach((el) => {
            try { el.scrollTop = 0; } catch { /* ignore */ }
        });
    }, [tab, mobileTab]);

    // Poll new-lead count for the bottom-nav badge
    useEffect(() => {
        const fetchCount = () => {
            api.get("/admin/stats").then((r) => {
                const c = (r.data?.inquiries?.new || 0) +
                          (r.data?.registrations?.new || 0) +
                          (r.data?.tutor_applications?.new || 0);
                setInboxCount(c);
            }).catch(() => {});
        };
        fetchCount();
        const t = setInterval(fetchCount, 60_000);
        return () => clearInterval(t);
    }, []);

    // V170.1 — Poll live-class count for the Classroom sidebar badge so admins
    // see mid-session activity without opening the Hub.
    useEffect(() => {
        const fetchLive = () => {
            api.get("/admin/classroom/hub-stats")
                .then((r) => setLiveClassCount(r.data?.live || 0))
                .catch(() => {});
        };
        fetchLive();
        const t = setInterval(fetchLive, 30_000);
        return () => clearInterval(t);
    }, []);

    const handleLogout = () => {
        logout();
        navigate("/admin/login");
    };

    // V173 — Close the mobile drawer on Escape + lock body scroll while open.
    useEffect(() => {
        if (!drawerOpen) return;
        const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [drawerOpen]);

    // Shared tab-click handler used by BOTH the desktop sidebar and the mobile
    // drawer. Keeps the existing manage-mode routing for mobile so panels
    // appear in the content area, and closes the drawer after selection.
    const onNavTabClick = (t) => {
        if (t.href) { navigate(t.href); setDrawerOpen(false); return; }
        setTab(t.id);
        if (t.id !== "stats") setMobileTab("manage");
        setDrawerOpen(false);
    };

    // When mobile-Manage routes the user into a specific module, switch to that tab
    const onMobileNavigate = (tabId) => {
        setTab(tabId);
        // 'manage' acts as a router; on mobile we use the desktop view content
        // but preserve mobileTab state so Back from the page restores Manage list
    };

    return (
        <AdminOrientationGuard>
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
            <AdminPwaInstallBanner />

            {/* V173 — Mobile sidebar drawer. The desktop sidebar (<aside>)
                below remains unchanged for lg+ users. On <lg this drawer is
                the only way to reach every admin tab directly — no more
                drilling through the Manage router for Invoices, Lead
                Notifications, Settings etc. */}
            {drawerOpen && (
                <div
                    className="lg:hidden fixed inset-0 z-50 flex"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Admin navigation menu"
                    data-testid="admin-mobile-drawer"
                >
                    {/* Backdrop */}
                    <button
                        type="button"
                        aria-label="Close navigation menu"
                        data-testid="admin-mobile-drawer-backdrop"
                        onClick={() => setDrawerOpen(false)}
                        className="absolute inset-0 bg-black/50"
                    />
                    {/* Panel */}
                    <div className="relative bg-white w-[82%] max-w-[320px] h-full shadow-2xl flex flex-col animate-[slideInLeft_180ms_ease-out]">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
                            <div className="flex items-center gap-2 min-w-0">
                                <img src={LOGO_URL} alt="" className="h-7 shrink-0" />
                                <div className="min-w-0">
                                    <div className="font-heading font-bold text-[#0F172A] text-sm truncate">Anand Tutorials</div>
                                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#3B82F6] inline-flex items-center gap-1">
                                        Admin · <SuperModeBadge />
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setDrawerOpen(false)}
                                data-testid="admin-mobile-drawer-close"
                                aria-label="Close menu"
                                className="p-2 -mr-1 rounded-lg hover:bg-slate-100 text-slate-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        {/* Scrollable nav */}
                        <nav className="flex-1 overflow-y-auto overscroll-contain px-2 py-3 space-y-4">
                            {visibleNav.map((g) => (
                                <div key={g.group}>
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-2 px-3">{g.group}</div>
                                    <div className="space-y-0.5">
                                        {g.items.map((t) => (
                                            <button
                                                key={t.id}
                                                onClick={() => onNavTabClick(t)}
                                                data-testid={`admin-mobile-tab-${t.id}`}
                                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-left transition-colors ${
                                                    tab === t.id
                                                        ? "bg-[#1E3A8A] text-white shadow-sm"
                                                        : "text-slate-700 hover:bg-slate-100 active:bg-slate-200"
                                                }`}
                                            >
                                                <t.icon className="w-4 h-4 shrink-0" />
                                                <span className="flex-1 truncate">{t.label}</span>
                                                {t.id === "assignments" && liveClassCount > 0 && (
                                                    <span
                                                        data-testid="drawer-live-class-badge"
                                                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold shrink-0 ${
                                                            tab === t.id ? "bg-white text-rose-600" : "bg-rose-500 text-white"
                                                        }`}
                                                    >
                                                        {liveClassCount} LIVE
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </nav>
                        {/* Footer actions in-drawer */}
                        <div className="border-t border-slate-200 p-3 space-y-1 shrink-0">
                            <a
                                href="/"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setDrawerOpen(false)}
                                data-testid="admin-mobile-drawer-view-site"
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
                            >
                                <ExternalLink className="w-4 h-4" /> View Site
                            </a>
                            <button
                                type="button"
                                onClick={() => { setDrawerOpen(false); handleLogout(); }}
                                data-testid="admin-mobile-drawer-logout"
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-rose-600 hover:bg-rose-50"
                            >
                                <LogOut className="w-4 h-4" /> Logout
                            </button>
                        </div>
                    </div>
                    <style>{`
                        @keyframes slideInLeft {
                            from { transform: translateX(-16px); opacity: 0.3 }
                            to { transform: translateX(0); opacity: 1 }
                        }
                    `}</style>
                </div>
            )}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
                <div className="w-full px-3 sm:px-6 lg:px-8 xl:px-10 py-3 flex items-center justify-between gap-2">
                    {/* V173 — Mobile hamburger: opens the sidebar drawer.
                        Hidden on lg+ because the full sidebar is always visible there. */}
                    <button
                        type="button"
                        onClick={() => setDrawerOpen(true)}
                        data-testid="admin-mobile-menu-btn"
                        aria-label="Open navigation menu"
                        aria-expanded={drawerOpen}
                        className="lg:hidden -ml-1 p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 text-slate-700 shrink-0"
                    >
                        <Menu className="w-6 h-6" />
                    </button>
                    <Link to="/" className="flex items-center gap-3 min-w-0 flex-1 lg:flex-initial">
                        <img src={LOGO_URL} alt="Anand Tutorials" className="h-9 shrink-0" />
                        <div className="min-w-0 hidden sm:block">
                            <div className="font-heading font-bold text-[#0F172A] inline-flex items-center gap-2">Anand Tutorials</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#3B82F6] inline-flex items-center gap-2">
                                Admin Control Center
                                <SuperModeBadge />
                            </div>
                        </div>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-3">
                        <Link to="/" target="_blank" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#1E3A8A]" data-testid="admin-view-site">
                            <ExternalLink className="w-4 h-4" /> View Site
                        </Link>
                        <span className="text-sm text-slate-600 hidden md:inline">Hi, {user?.name || "Admin"}</span>
                        <LeadNotificationBell onNavigate={(tabId, item) => {
                            setTab(tabId);
                            if (tabId !== "stats") setMobileTab("manage");
                            if (item?.id) {
                                setAutoOpen((prev) => ({ tabId, id: item.id, seq: (prev?.seq || 0) + 1 }));
                            }
                        }} />
                        <SuperModeToggle />
                        <button onClick={handleLogout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 px-2 sm:px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">
                            <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
                        </button>
                    </div>
                </div>
                {/* Universal search bar (second row — full width on mobile, centered on desktop) */}
                <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 pb-3 flex justify-center">
                    <UniversalSearch onNavigate={(tabId, item) => {
                        setTab(tabId);
                        if (tabId !== "stats") setMobileTab("manage");
                        if (item?.id) {
                            setAutoOpen((prev) => ({ tabId, id: item.id, seq: (prev?.seq || 0) + 1 }));
                        }
                    }} />
                </div>
            </header>

            <div className="w-full lg:px-8 xl:px-10 lg:py-8 lg:grid lg:grid-cols-[240px_1fr] lg:gap-8">
                <aside className="hidden lg:block space-y-5">
                    {visibleNav.map((g) => (
                        <div key={g.group}>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-2 px-3">{g.group}</div>
                            <div className="space-y-1">
                                {g.items.map((t) => (
                                    <button
                                        key={t.id}
                                        onClick={() => onNavTabClick(t)}
                                        data-testid={`admin-tab-${t.id}`}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            tab === t.id
                                                ? "bg-[#1E3A8A] text-white shadow-sm"
                                                : "text-slate-700 hover:bg-white hover:text-[#1E3A8A]"
                                        }`}
                                    >
                                        <t.icon className="w-4 h-4" />
                                        <span className="flex-1 text-left">{t.label}</span>
                                        {t.id === "assignments" && liveClassCount > 0 && (
                                            <span
                                                data-testid="sidebar-live-class-badge"
                                                title={`${liveClassCount} class${liveClassCount === 1 ? "" : "es"} live now`}
                                                aria-label={`${liveClassCount} class${liveClassCount === 1 ? "" : "es"} live now`}
                                                className={`relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                                                    tab === t.id ? "bg-white text-rose-600" : "bg-rose-500 text-white"
                                                }`}
                                            >
                                                <span className="relative flex h-1.5 w-1.5" aria-hidden>
                                                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${tab === t.id ? "bg-rose-500" : "bg-white"}`} />
                                                    <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${tab === t.id ? "bg-rose-600" : "bg-white"}`} />
                                                </span>
                                                {liveClassCount} LIVE
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </aside>

                {/* Desktop main content (hidden on mobile) */}
                <main className="hidden lg:block min-w-0">
                    {tab === "stats" && <StatsView onJump={setTab} />}
                    {tab === "pipeline" && <LeadManagementPanel />}
                    {tab === "time-converter" && <TimeConverterPanel />}
                    {tab === "conversions" && <ConversionsPanel />}
                    {tab === "reviews" && <ReviewsModerationPanel />}
                    {tab === "unified-inbox" && <UnifiedInbox />}
                    {tab === "inquiries" && <LeadsView kind="inquiries" title="Contact Inquiries" autoOpen={autoOpen.tabId === "inquiries" ? autoOpen : null} />}
                    {tab === "registrations" && <LeadsView kind="registrations" title="Demo Registrations" autoOpen={autoOpen.tabId === "registrations" ? autoOpen : null} />}
                    {tab === "assignments" && <AssignmentsView />}
                    {tab === "tutor-applications" && <LeadsView kind="tutor-applications" title="Tutor Applications" autoOpen={autoOpen.tabId === "tutor-applications" ? autoOpen : null} />}
                    {tab === "payment-consents" && <PaymentConsentsPanel />}
                    {tab === "payment-submissions" && <PaymentSubmissionsPanel />}
                    {tab === "payment-links" && <PaymentLinksPanel />}
                    {tab === "fee-reminders" && <FeeRemindersPanel />}
                    {tab === "fee-recommendation" && <FeeRecommendationPanel prefill={feeRecPrefill} />}
                    {tab === "invoices" && <InvoicesPanel autoOpen={autoOpen.tabId === "invoices" ? autoOpen : null} />}
                    {tab === "app-leads" && <AppLeadsView />}
                    {tab === "ongoing-tuitions" && <OngoingTuitionsPanel />}
                    {tab === "blogs" && <BlogsView />}
                    {tab === "policies" && <PoliciesView />}
                    {tab === "seo" && <SeoMetaPanel />}
                    {tab === "seo-insights" && <SeoInsightsPanel onNavigate={setTab} />}
                    {tab === "gbp-profiles" && <GbpProfilesPanel />}
                    {tab === "team-management" && <TeamManagementPanel />}
                    {tab === "tutor-email-templates" && <TutorEmailTemplatesPanel />}
                    {tab === "ai-marketing" && <AiMarketingPanel />}
                    {tab === "settings" && <SettingsPanel onJump={setTab} hiddenModules={HIDDEN_DEV_MODULES} />}
                    {tab === "lead-delivery" && <LeadDeliveryPanel />}
                    {tab === "master-data" && <MasterDataPanel />}
                    {tab === "unresolved-cities" && <UnresolvedCitiesPanel onOpenRecord={(s) => { const tabId = s.source === "inquiry" ? "inquiries" : s.source === "tutor" ? "tutor-applications" : "registrations"; setTab(tabId); setAutoOpen({ tabId, id: s.id, seq: Date.now() }); }} />}
                    {tab === "tutor-portal" && <TutorPortalSettings />}
                    {tab === "photo-management" && <PhotoManagementPanel />}
                    {tab === "login-activity" && <LoginActivityPanel />}
                    {tab === "bulk-delete" && <BulkDeleteSection />}
                    {tab === "profile" && <ProfilePanel />}
                </main>

                {/* Mobile content — Wix-style 4-tab layout */}
                <main className="lg:hidden pb-20" data-testid="mobile-admin-main">
                    {/* If user drilled into a specific module from Manage, show that module */}
                    {mobileTab === "manage" && tab !== "stats" ? (
                        <div className="px-4 py-3">
                            <button
                                onClick={() => setTab("stats")}
                                data-testid="mobile-back-to-manage"
                                className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A] mb-2"
                            >
                                ← Back to Manage
                            </button>
                            <div className="-mx-4">
                                {tab === "blogs" && <BlogsView />}
                                {tab === "policies" && <PoliciesView />}
                                {tab === "seo" && <SeoMetaPanel />}
                                {tab === "seo-insights" && <SeoInsightsPanel onNavigate={setTab} />}
                                {tab === "gbp-profiles" && <GbpProfilesPanel />}
                                {tab === "team-management" && <TeamManagementPanel />}
                                {tab === "tutor-email-templates" && <TutorEmailTemplatesPanel />}
                                {tab === "ai-marketing" && <AiMarketingPanel />}
                                {tab === "settings" && <SettingsPanel onJump={setTab} hiddenModules={HIDDEN_DEV_MODULES} />}
                    {tab === "lead-delivery" && <LeadDeliveryPanel />}
                                {tab === "master-data" && <MasterDataPanel />}
                                {tab === "unresolved-cities" && <UnresolvedCitiesPanel onOpenRecord={(s) => { const tabId = s.source === "inquiry" ? "inquiries" : s.source === "tutor" ? "tutor-applications" : "registrations"; setTab(tabId); setAutoOpen({ tabId, id: s.id, seq: Date.now() }); }} />}
                    {tab === "tutor-portal" && <TutorPortalSettings />}
                                {tab === "photo-management" && <PhotoManagementPanel />}
                                {tab === "login-activity" && <LoginActivityPanel />}
                                {tab === "bulk-delete" && <BulkDeleteSection />}
                                {tab === "profile" && <ProfilePanel />}
                                {tab === "tutor-applications" && <LeadsView kind="tutor-applications" title="Tutor Applications" autoOpen={autoOpen.tabId === "tutor-applications" ? autoOpen : null} />}
                                {tab === "unified-inbox" && <UnifiedInbox />}
                                {tab === "registrations" && <LeadsView kind="registrations" title="Demo Registrations" autoOpen={autoOpen.tabId === "registrations" ? autoOpen : null} />}
                                {tab === "inquiries" && <LeadsView kind="inquiries" title="Contact Inquiries" autoOpen={autoOpen.tabId === "inquiries" ? autoOpen : null} />}
                                {tab === "payment-consents" && <PaymentConsentsPanel />}
                                {tab === "payment-submissions" && <PaymentSubmissionsPanel />}
                                {tab === "payment-links" && <PaymentLinksPanel />}
                                {tab === "fee-reminders" && <FeeRemindersPanel />}
                                {tab === "fee-recommendation" && <FeeRecommendationPanel prefill={feeRecPrefill} />}
                    {tab === "invoices" && <InvoicesPanel autoOpen={autoOpen.tabId === "invoices" ? autoOpen : null} />}
                                {tab === "app-leads" && <AppLeadsView />}
                                {tab === "ongoing-tuitions" && <OngoingTuitionsPanel />}
                                {tab === "conversions" && <ConversionsPanel />}
                                {tab === "time-converter" && <TimeConverterPanel />}
                                {tab === "reviews" && <ReviewsModerationPanel />}
                                {tab === "assignments" && <AssignmentsView />}
                            </div>
                        </div>
                    ) : (
                        <>
                            {mobileTab === "home" && (
                                <div className="px-4 py-4">
                                    <div className="mb-4">
                                        <div className="text-xs uppercase tracking-[0.2em] text-[#3B82F6] font-bold">Welcome back</div>
                                        <h2 className="font-heading text-2xl font-bold text-[#0F172A] mt-0.5">Hi {user?.name || "Admin"}</h2>
                                    </div>
                                    <StatsView onJump={(t) => { setTab(t); setMobileTab(t === "tutor-applications" ? "manage" : "inbox"); }} />
                                </div>
                            )}
                            {mobileTab === "analytics" && <MobileAnalytics />}
                            {mobileTab === "inbox" && <MobileInbox onCountChange={setInboxCount} />}
                            {mobileTab === "manage" && tab === "stats" && (
                                <MobileManage
                                    onNavigate={(t) => { setTab(t); }}
                                    liveClassCount={liveClassCount}
                                />
                            )}
                        </>
                    )}
                </main>
            </div>

            <MobileBottomNav
                active={mobileTab}
                onChange={(t) => { setMobileTab(t); if (t !== "manage") setTab("stats"); }}
                inboxCount={inboxCount}
            />

            {/* V21 Phase 1 — Mobile Floating Action Button (FAB) */}
            <AdminMobileFab
                onCreateDemoLead={() => setCreateLeadOpen(true)}
                onAddTutor={() => { setTab("tutor-applications"); setMobileTab("manage"); }}
                onAddFollowUp={() => {
                    // Focus the note input on the currently-open lead modal, else navigate to Inbox
                    const el = document.querySelector('[data-testid="lead-note-input"]');
                    if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus(); }
                    else { setMobileTab("inbox"); }
                }}
            />

            {createLeadOpen && (
                <CreateDemoLeadModal
                    open={createLeadOpen}
                    onClose={() => setCreateLeadOpen(false)}
                    onCreated={() => { setCreateLeadOpen(false); setMobileTab("inbox"); }}
                />
            )}

            {/* V90 — Discreet build badge (bottom-left) so admins can always
                confirm which release is live at a glance. Click to reveal the
                full release manifest fetched from GET /api/health/version. */}
            <BuildBadge />
        </div>
        </AdminOrientationGuard>
    );
}

function BuildBadge() {
    const [info, setInfo] = useState(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        let cancelled = false;
        api.get("/health/version")
            .then((r) => { if (!cancelled) setInfo(r.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);
    if (!info) return null;
    const commitDate = info.commit_time ? new Date(info.commit_time).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
    return (
        <div className="fixed left-2 bottom-2 z-30 print:hidden">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="admin-build-badge"
                title={`Build ${info.version} · deployed ${commitDate}`}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-900/85 text-white text-[10px] font-mono tracking-wide hover:bg-slate-900 shadow-lg backdrop-blur-sm"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                {info.version}
            </button>
            {open && (
                <div
                    data-testid="admin-build-badge-panel"
                    className="absolute left-0 bottom-8 w-72 bg-white border border-slate-200 shadow-2xl rounded-lg p-3 text-xs"
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="font-bold text-slate-900">Release {info.version}</div>
                        <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700 text-lg leading-none">×</button>
                    </div>
                    <div className="text-[11px] text-slate-500 mb-2">
                        Deployed: <span className="text-slate-700">{commitDate}</span>
                        <br />
                        Uptime: <span className="text-slate-700">{Math.floor(info.uptime_seconds / 60)} min</span>
                    </div>
                    <div className="border-t border-slate-100 pt-2 max-h-60 overflow-y-auto space-y-1">
                        {(info.releases || []).map((r) => (
                            <div key={r.v} className="flex items-start gap-2">
                                <span className="font-mono text-[10px] text-rose-600 font-bold w-8 shrink-0">{r.v}</span>
                                <span className="text-[11px] text-slate-700">{r.note}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatsView({ onJump }) {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);
    if (!stats) return <Loader />;

    const cards = [
        { id: "inquiries", label: "Contact Inquiries", v: stats.inquiries.total, hl: stats.inquiries.new, hlLabel: "new", breakdown: stats.inquiries },
        { id: "registrations", label: "Demo Registrations", v: stats.registrations.total, hl: stats.registrations.new, hlLabel: "new", breakdown: stats.registrations },
        { id: "tutor-applications", label: "Tutor Applications", v: stats.tutor_applications.total, hl: stats.tutor_applications.new, hlLabel: "to review", breakdown: stats.tutor_applications },
        { id: "blogs", label: "Blog Posts", v: stats.blogs },
    ];

    return (
        <div data-testid="admin-stats">
            <h2 className="font-heading text-2xl font-bold mb-1">Overview</h2>
            <p className="text-sm text-slate-500 mb-6">Live snapshot of leads, applications and content.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {cards.map((c) => (
                    <button
                        key={c.label}
                        onClick={() => c.id !== "blogs" && onJump(c.id)}
                        className="text-left bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-sm transition-all"
                    >
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1">{c.label}</div>
                        <div className="font-heading text-3xl font-bold text-[#0F172A]">{c.v}</div>
                        {c.hl !== undefined && c.hl > 0 && (
                            <div className="mt-1 text-xs text-amber-600 font-semibold">{c.hl} {c.hlLabel}</div>
                        )}
                    </button>
                ))}
            </div>

            {/* status breakdown grid */}
            <div className="grid md:grid-cols-3 gap-4">
                {["inquiries", "registrations", "tutor_applications"].map((k) => {
                    const data = stats[k];
                    if (!data) return null;
                    const label = k === "tutor_applications" ? "Tutor Applications" : k === "inquiries" ? "Inquiries" : "Registrations";
                    const entries = Object.entries(data).filter(([key]) => key !== "total");
                    return (
                        <div key={k} className="bg-white border border-slate-200 rounded-2xl p-5">
                            <div className="text-sm font-heading font-semibold text-[#0F172A] mb-3">{label} by status</div>
                            <div className="space-y-1.5">
                                {entries.map(([s, n]) => (
                                    <div key={s} className="flex items-center justify-between text-sm">
                                        <span className="text-slate-600 capitalize">{s.replace(/_/g, " ")}</span>
                                        <span className="font-semibold text-[#0F172A]">{n}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function BlogsView() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [filter, setFilter] = useState("all"); // "all" | "qa"
    const [approving, setApproving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/admin/blogs").then((r) => setBlogs(r.data)).catch(() => {}).finally(() => setLoading(false));
    };
    useEffect(load, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this blog post?")) return;
        try {
            await api.delete(`/admin/blogs/${id}`);
            toast.success("Deleted");
            load();
        } catch {
            toast.error("Failed to delete");
        }
    };

    const approveOne = async (id) => {
        try {
            await api.post(`/admin/blogs/${id}/approve`);
            toast.success("Marked reviewed");
            load();
        } catch {
            toast.error("Failed to approve");
        }
    };

    const approveAll = async () => {
        if (!window.confirm(`Approve all ${pendingCount} pending blogs? This clears the QA Review flag on each.`)) return;
        setApproving(true);
        try {
            const r = await api.post(`/admin/blogs/approve-all`);
            toast.success(`Approved ${r.data?.approved ?? 0} blogs`);
            load();
        } catch {
            toast.error("Failed to bulk approve");
        } finally {
            setApproving(false);
        }
    };

    const pendingCount = blogs.filter((b) => b.qa_review).length;
    const visibleBlogs = filter === "qa" ? blogs.filter((b) => b.qa_review) : blogs;

    if (loading) return <Loader />;

    return (
        <div data-testid="admin-blogs">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex items-center gap-3">
                    <h2 className="font-heading text-2xl font-bold">Blog Posts</h2>
                    {pendingCount > 0 && (
                        <span data-testid="qa-pending-badge" className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-800 font-semibold">
                            {pendingCount} pending QA
                        </span>
                    )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-sm">
                        <button onClick={() => setFilter("all")} data-testid="filter-all-blogs" className={`px-3 py-1.5 ${filter === "all" ? "bg-[#0F172A] text-white" : "bg-white text-slate-700"}`}>All ({blogs.length})</button>
                        <button onClick={() => setFilter("qa")} data-testid="filter-qa-blogs" className={`px-3 py-1.5 ${filter === "qa" ? "bg-amber-600 text-white" : "bg-white text-slate-700"}`}>Pending QA ({pendingCount})</button>
                    </div>
                    {pendingCount > 0 && (
                        <button onClick={approveAll} disabled={approving} data-testid="approve-all-blogs" className="inline-flex items-center gap-1.5 bg-amber-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
                            Approve all
                        </button>
                    )}
                    <button onClick={() => setEditing({ title: "", excerpt: "", content: "", category: "General", cover_image: "", tags: "", published: true })} data-testid="admin-new-blog" className="inline-flex items-center gap-1.5 bg-[#1E3A8A] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#1E40AF]">
                        <Plus className="w-4 h-4" /> New Post
                    </button>
                </div>
            </div>
            {visibleBlogs.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
                    {filter === "qa" ? "No blogs awaiting QA review. Great work!" : "No blog posts yet."}
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-4">
                    {visibleBlogs.map((b) => (
                        <div key={b.id} className={`bg-white border rounded-xl p-5 ${b.qa_review ? "border-amber-300 ring-1 ring-amber-100" : "border-slate-200"}`}>
                            <div className="flex items-start justify-between mb-2 gap-2 flex-wrap">
                                <div className="flex items-center gap-1.5">
                                    <span className={`text-xs px-2 py-0.5 rounded ${b.published ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                                        {b.published ? "Published" : "Draft"}
                                    </span>
                                    {b.qa_review && (
                                        <span data-testid={`qa-flag-${b.id}`} className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
                                            QA Review
                                        </span>
                                    )}
                                </div>
                                <div className="flex gap-1">
                                    {b.qa_review && (
                                        <button onClick={() => approveOne(b.id)} data-testid={`approve-blog-${b.id}`} title="Mark as reviewed" className="p-1.5 hover:bg-green-50 rounded text-green-600 text-xs font-semibold">
                                            ✓ Approve
                                        </button>
                                    )}
                                    <button onClick={() => setEditing({ ...b, tags: (b.tags || []).join(", ") })} data-testid={`edit-blog-${b.id}`} className="p-1.5 hover:bg-slate-100 rounded"><Pencil className="w-4 h-4 text-slate-500" /></button>
                                    <button onClick={() => remove(b.id)} data-testid={`delete-blog-${b.id}`} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4 text-red-500" /></button>
                                </div>
                            </div>
                            <h3 className="font-heading font-semibold text-[#0F172A] mb-1">{b.title}</h3>
                            <p className="text-xs text-slate-500 mb-2">{b.category} · {new Date(b.created_at).toLocaleDateString()}</p>
                            <p className="text-sm text-slate-600 line-clamp-2">{b.excerpt}</p>
                        </div>
                    ))}
                </div>
            )}
            {editing && <BlogEditor blog={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function BlogEditor({ blog, onClose, onSaved }) {
    const [form, setForm] = useState(blog);
    const [saving, setSaving] = useState(false);
    const isEdit = !!blog.id;

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const save = async () => {
        if (!form.title || !form.excerpt || !form.content) {
            toast.error("Title, excerpt and content are required.");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags,
            };
            delete payload.id;
            delete payload.slug;
            delete payload.created_at;
            delete payload.updated_at;
            if (isEdit) await api.put(`/admin/blogs/${blog.id}`, payload);
            else await api.post("/admin/blogs", payload);
            toast.success(isEdit ? "Updated" : "Created");
            onSaved();
        } catch {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                    <h3 className="font-heading text-lg font-semibold">{isEdit ? "Edit Post" : "New Post"}</h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <Field label="Title *"><input value={form.title || ""} onChange={update("title")} data-testid="blog-input-title" className={inputCls} /></Field>
                    <Field label="Excerpt *"><textarea value={form.excerpt || ""} onChange={update("excerpt")} rows={2} data-testid="blog-input-excerpt" className={`${inputCls} resize-none`} /></Field>
                    <Field label="Cover Image URL"><input value={form.cover_image || ""} onChange={update("cover_image")} data-testid="blog-input-cover" className={inputCls} /></Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Category"><input value={form.category || ""} onChange={update("category")} data-testid="blog-input-category" className={inputCls} /></Field>
                        <Field label="Tags (comma separated)"><input value={form.tags || ""} onChange={update("tags")} data-testid="blog-input-tags" className={inputCls} /></Field>
                    </div>
                    <Field label="Content (HTML allowed) *">
                        <textarea value={form.content || ""} onChange={update("content")} rows={10} data-testid="blog-input-content" className={`${inputCls} font-mono text-xs resize-y`} />
                    </Field>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.published} onChange={update("published")} data-testid="blog-input-published" />
                        Published (visible on public site)
                    </label>
                </div>
                <div className="p-6 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="blog-save" className="inline-flex items-center gap-2 px-5 py-2 bg-[#1E3A8A] text-white text-sm font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const Loader = () => (
    <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
);

function PoliciesView() {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/policies").then((r) => setPolicies(r.data)).catch(() => {}).finally(() => setLoading(false));
    };
    useEffect(load, []);

    if (loading) return <Loader />;
    return (
        <div data-testid="admin-policies">
            <h2 className="font-heading text-2xl font-bold mb-1">Manage Policies</h2>
            <p className="text-sm text-slate-600 mb-6">Edit Terms, Privacy, Refund, Tutor and Parent policies. Changes appear on the public site instantly.</p>
            <div className="grid md:grid-cols-2 gap-4">
                {policies.map((p) => (
                    <div key={p.slug} className="bg-white border border-slate-200 rounded-xl p-5">
                        <div className="flex items-start justify-between mb-2">
                            <div>
                                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#3B82F6]">{p.eyebrow || "Policy"}</span>
                                <h3 className="font-heading font-semibold text-[#0F172A] text-lg">{p.title}</h3>
                            </div>
                            <button onClick={() => setEditing(p)} data-testid={`edit-policy-${p.slug}`} className="p-1.5 hover:bg-slate-100 rounded">
                                <Pencil className="w-4 h-4 text-slate-500" />
                            </button>
                        </div>
                        <p className="text-xs text-slate-500">/policies/{p.slug} · {(p.sections || p.section_count || []).length || "—"} sections</p>
                    </div>
                ))}
            </div>
            {editing && <PolicyEditor policy={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function PolicyEditor({ policy, onClose, onSaved }) {
    const [form, setForm] = useState({
        title: policy.title || "",
        sections: policy.sections || [],
    });
    const [saving, setSaving] = useState(false);

    const updateSection = (i, key, value) => {
        const next = [...form.sections];
        next[i] = { ...next[i], [key]: value };
        setForm({ ...form, sections: next });
    };
    const addSection = () => setForm({ ...form, sections: [...form.sections, { heading: "", body: "" }] });
    const removeSection = (i) => setForm({ ...form, sections: form.sections.filter((_, idx) => idx !== i) });

    const save = async () => {
        if (!form.title) {
            toast.error("Title is required");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/admin/policies/${policy.slug}`, form);
            toast.success("Policy updated");
            onSaved();
        } catch {
            toast.error("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
                    <h3 className="font-heading text-lg font-semibold">Edit: {policy.title}</h3>
                    <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-4">
                    <Field label="Title">
                        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="policy-input-title" className={inputCls} />
                    </Field>
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Sections</span>
                            <button onClick={addSection} data-testid="policy-add-section" className="inline-flex items-center gap-1 text-xs text-[#1E3A8A] font-semibold hover:underline">
                                <Plus className="w-3 h-3" /> Add section
                            </button>
                        </div>
                        <div className="space-y-3">
                            {form.sections.map((s, i) => (
                                <div key={i} className="border border-slate-200 rounded-xl p-4 bg-slate-50">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs text-slate-500">Section {i + 1}</span>
                                        <button onClick={() => removeSection(i)} data-testid={`policy-remove-section-${i}`} className="p-1 hover:bg-red-50 rounded">
                                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                        </button>
                                    </div>
                                    <input value={s.heading} onChange={(e) => updateSection(i, "heading", e.target.value)} placeholder="Section heading" data-testid={`policy-section-heading-${i}`} className={`${inputCls} mb-2 font-semibold`} />
                                    <textarea value={s.body} onChange={(e) => updateSection(i, "body", e.target.value)} placeholder="Section body" rows={3} data-testid={`policy-section-body-${i}`} className={`${inputCls} resize-y`} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-6 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="policy-save" className="inline-flex items-center gap-2 px-5 py-2 bg-[#1E3A8A] text-white text-sm font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {saving ? "Saving..." : "Save Policy"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#3B82F6]";
const Field = ({ label, children }) => (
    <label className="block">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 mb-1.5 block">{label}</span>
        {children}
    </label>
);
